"use client";

/**
 * Client-side shell for the homepage.
 *
 * Extracted from page.tsx so the homepage can be a Server Component.
 * This component owns the only client-side logic the homepage needs:
 *   - Geolocation resolution (deferred until browser idle)
 *   - Referral route detection for the banner
 */

import { useState, useEffect, useSyncExternalStore } from "react";
import HeroSection from "@/components/home/HeroSection";
import ReferralBanner from "@/components/home/ReferralBanner";
import { logClientEvent } from "@/utils/logUtils";
import { getLocation } from "@/utils/locationUtils";
import { STATES_SELLING_INSURANCE } from "@/config/supportedStates";

export default function HomeClientShell() {
  const [userCity, setUserCity] = useState<string>("");

  const pathname = useSyncExternalStore(
    (onStoreChange) => {
      window.addEventListener("popstate", onStoreChange);
      window.addEventListener("hashchange", onStoreChange);
      return () => {
        window.removeEventListener("popstate", onStoreChange);
        window.removeEventListener("hashchange", onStoreChange);
      };
    },
    () => window.location.pathname,
    () => "/",
  );

  const isReferralsRoute =
    pathname === "/referrals" || pathname === "/new-home-start";

  // Resolve user location after initial paint
  useEffect(() => {
    const fetchUserCity = async () => {
      try {
        const location = await getLocation();

        const country = location?.country_name ?? "";
        const region = location?.region_name ?? "";
        const city = location?.city ?? "";

        let resolvedCity = "";

        if (
          country === "United States" &&
          STATES_SELLING_INSURANCE.includes(region)
        ) {
          if (city && region) {
            resolvedCity = `in ${city}`;
          }
        }

        setUserCity(resolvedCity);

        logClientEvent("Home: getLocation resolved", {
          rawCity: city,
          rawRegion: region,
          rawCountry: country,
          finalUserCity: resolvedCity,
          timestamp: new Date().toISOString(),
        });
      } catch (error) {
        console.error("Error fetching user location:", error);

        logClientEvent("Geolocation fetch failed", {
          error: error instanceof Error ? error.message : "Unknown error",
          finalUserCity: "",
          timestamp: new Date().toISOString(),
        });
      }
    };

    if ("requestIdleCallback" in window) {
      const idleCallbackId = requestIdleCallback(fetchUserCity);
      return () => cancelIdleCallback(idleCallbackId);
    } else {
      const timeoutId = setTimeout(fetchUserCity, 1);
      return () => clearTimeout(timeoutId);
    }
  }, []);

  return (
    <>
      {isReferralsRoute ? <ReferralBanner /> : null}
      <HeroSection userCity={userCity} />
    </>
  );
}
